/**
 * Primary vertical nav tree for **BHYBE navigation shell only**.
 * Classic Materio layout uses {@link VerticalNavItems} from `src/gyg-navigation/vertical` unchanged.
 * Must be a `use*` hook (uses GrowthBook + auth hooks).
 */

import { useGrowthBook } from '@/src/context/GrowthBookProvider'
import { FeatureFlag } from '@/src/types/common'
import { VerticalNavItemsType } from 'src/@core/layouts/types'
import { useAuth } from '@/src/hooks/useAuth'
import { ROUTES } from '@/src/utils/routes'
import { getAskGygUrl } from '@/src/modules/ask-gyg/url'
import { GYG_NAV_LABELS } from '@/src/gyg-navigation/navLabels'
import { getSplitAdminNavGroups } from '@/src/gyg-navigation/splitAdminNavGroups'

export const useBhyvePrimaryNavItems = (growthbook?: any, auth?: any): VerticalNavItemsType => {
  // Always call hooks first, then use provided values if available
  const hookGrowthbook = useGrowthBook()
  const hookAuth = useAuth()

  const growthbookData = growthbook || hookGrowthbook
  const authData = auth || hookAuth

  const isMasterProductListEnabled = growthbookData?.isOn(FeatureFlag.MASTER_PRODUCT_LIST) || false
  const isFranchiseModuleEnabled = growthbookData?.isOn(FeatureFlag.FRANCHISE_HUB) || false
  const isOrderManagementEnabled = growthbookData?.isOn(FeatureFlag.ORDER_MANAGEMENT) || false
  const isKitchenManagementEnabled = growthbookData?.isOn(FeatureFlag.KITCHEN_MANAGEMENT) || false

  // TODO: Re-enable with GYG Oracle menu.
  // const isRestaurantInsightsEnabled = growthbookData?.isOn(FeatureFlag.RESTAURANT_INSIGHTS) || false
  const isDigitalTrainingPlannerEnabled = growthbookData?.isOn(FeatureFlag.DIGITAL_TRAINING_PLANNER) || false
  const isAskGygEnabled = (growthbookData?.isOn(FeatureFlag.ASK_GYG) || false) && !!getAskGygUrl()

  return [
    {
      title: 'Home',
      path: ROUTES.RESTAURANT,
      icon: 'tabler:home',
      subject: 'restaurant',
      action: 'read'
    },
    ...(isAskGygEnabled
      ? [
          {
            title: 'Ask GYG',
            icon: 'tabler:robot',
            path: '/app/ask-gyg',
            customPermissionCheck: (user: any) => {
              if (user?.role?.id === 1) return true
              const handle = user?.role?.handle

              return handle === 'COC' || handle === 'SCOC' || handle === 'ROM' || handle === 'STA'
            }
          }
        ]
      : []),
    {
      title: GYG_NAV_LABELS.restaurants,
      icon: 'mynaui:store',
      subject: 'restaurant',
      action: 'read',
      children: [
        {
          title: GYG_NAV_LABELS.restaurantAllocation,
          path: '/app/restaurant/allocation',
          icon: 'mdi:account-multiple',
          subject: 'restaurant-allocation',
          action: 'read',

          // Custom check: requires both restaurant-allocation and restaurant-delegation read permissions
          customPermissionCheck: (user: any) => {
            const hasAllocationRead = user?.permissions?.['restaurant-allocation']?.includes('read') || false
            const hasDelegationRead = user?.permissions?.['restaurant-delegation']?.includes('read') || false

            return hasAllocationRead && hasDelegationRead
          }
        },
        {
          title: GYG_NAV_LABELS.permanentDisableRequests,
          path: '/app/restaurant-menu-equipments/permanent-disable-requests',
          icon: 'mdi:power-plug-off-outline',
          subject: 'menu',
          action: 'read'
        },
        {
          title: 'Menu Jobs',
          path: '/app/incomplete-menu-jobs',
          icon: 'bxs:menu',
          subject: 'job-management',
          action: 'read'
        }
      ]
    },
    {
      title: GYG_NAV_LABELS.reporting,
      icon: 'mdi:chart-box-outline',
      subject: 'restaurant',
      action: 'read',
      children: [
        {
          title: GYG_NAV_LABELS.disabledIngredients,
          path: '/app/disabled-ingredients',
          icon: 'bxs:disabled-ingredients',
          subject: 'restaurant',
          action: 'read'
        },
        {
          title: GYG_NAV_LABELS.disabledEquipments,
          path: '/app/disabled-equipments',
          icon: 'mdi:toaster-off',
          subject: 'restaurant-menu-equipment',
          action: 'read'
        },
        {
          title: GYG_NAV_LABELS.weeklyDisabledIngredientsReport,
          path: '/app/disabled-ingredients/reports',
          icon: 'mdi:file-chart-outline',
          subject: 'restaurant',
          action: 'read'
        },
        {
          title: GYG_NAV_LABELS.weeklyChangesReport,
          path: '/app/audit-logs/reports',
          icon: 'mdi:file-document-multiple-outline',
          subject: 'restaurant',
          action: 'read'
        },
        {
          title: GYG_NAV_LABELS.coachTenureReport,
          path: '/app/coach-tenure/reports',
          icon: 'mdi:account-clock-outline',
          subject: 'restaurant',
          action: 'read'
        },
        {
          title: GYG_NAV_LABELS.operationalReports,
          path: '/app/submissions',
          icon: 'mynaui:store',
          subject: 'restaurant',
          action: 'read'
        }
      ]
    },
    {
      title: GYG_NAV_LABELS.marketing,
      icon: 'pixelarticons:chart',
      subject: 'marketing',
      action: 'read',
      children: [
        {
          title: 'Offers',
          path: '/app/marketing-portal/offers',
          icon: 'bxs:offer',
          subject: 'marketing',
          action: 'read'
        },
        {
          title: GYG_NAV_LABELS.restaurantSurcharges,
          path: '/app/marketing-portal/store-surcharges',
          icon: 'bxs:offer',
          subject: 'marketing',
          action: 'read'
        },
        {
          title: 'Nutritional Info',
          path: '/app/marketing-portal/nutritional-info',
          subject: 'ingredient-nutritional-info',
          action: 'read'
        },
        {
          title: 'Nutritional Used Quantity',
          path: '/app/marketing-portal/nutritional-info/used-quantity',
          subject: 'ingredient-nutritional-info',
          action: 'read'
        }
      ]
    },
    ...(isKitchenManagementEnabled
      ? [
          {
            title: 'Kitchen Management',
            icon: 'mdi:chef-hat',
            subject: 'kitchen-management',
            action: 'read',
            children: [
              {
                title: 'OMS Restaurants',
                path: '/app/kitchen-management/oms-restaurants',
                icon: 'mdi:store',
                subject: 'kitchen-management',
                action: 'read'
              },
              {
                title: 'OMS Apps',
                path: '/app/kitchen-management/oms-apps',
                icon: 'mdi:application-outline',
                subject: 'kitchen-management',
                action: 'read'
              },
              {
                title: 'OMS Apps Versions',
                path: '/app/kitchen-management/oms-app-versions',
                icon: 'mdi:tag-multiple-outline',
                subject: 'kitchen-management.app-versions',
                action: 'read'
              },
              {
                title: 'OMS Products',
                path: '/app/kitchen-management/oms-products',
                icon: 'mdi:food',
                subject: 'kitchen-management',
                action: 'read'
              },
              {
                title: 'OMS Bundles',
                path: '/app/kitchen-management/oms-bundles',
                icon: 'mdi:food',
                subject: 'kitchen-management',
                action: 'read'
              },
              {
                title: 'OMS Settings',
                path: '/app/kitchen-management/oms-settings',
                icon: 'mdi:cog',
                subject: 'kitchen-management',
                action: 'read'
              }
            ]
          }
        ]
      : []),
    {
      title: GYG_NAV_LABELS.foodGuide,
      icon: 'mdi:book-open-outline',
      subject: 'recipe',
      action: 'read',
      children: [
        {
          title: GYG_NAV_LABELS.recipes,
          path: '/app/recipe-mgmt',
          icon: 'mdi:account-group',
          subject: 'recipe',
          action: 'read'
        },
        {
          title: GYG_NAV_LABELS.recipeEquipment,
          path: '/app/recipe-guide-equipments',
          icon: 'mdi:account-group',
          subject: 'recipe-guide-equipment',
          action: 'read'
        },
        {
          title: 'Hazard Tags',
          path: '/app/food-safety-tags',
          icon: 'mdi:tag',
          subject: 'recipe',
          action: 'read'
        },
        {
          title: 'Portioning Tools',
          path: '/app/portioning-tools',
          icon: 'mdi:ruler',
          subject: 'portioning-tool',
          action: 'read'
        },
        {
          title: 'Ingredients',
          path: '/app/recipe-guide-ingredients',
          icon: 'mdi:account-group',
          subject: 'recipe',
          action: 'read'
        },
        {
          title: 'Build Sections',
          path: '/app/build-sections',
          icon: 'mdi:view-grid-outline',
          subject: 'build-section',
          action: 'read'
        },
        {
          title: 'Splash Screens',
          path: '/app/splash-screens',
          icon: 'mdi:image-outline',
          subject: 'recipe',
          action: 'read'
        },
        {
          title: 'Recipe Making Tips',
          path: '/app/recipe-making-tips',
          icon: 'mdi:lightbulb-on-outline',
          subject: 'recipe',
          action: 'read'
        },
        {
          title: 'QRG Cafe Hola',
          path: '/app/recipe-qrg',
          icon: 'mdi:file-pdf-box',
          subject: 'recipe',
          action: 'update'
        }
      ]
    },
    {
      title: GYG_NAV_LABELS.guestExperience,
      icon: 'ant-design:customer-service-outlined',
      path: '/app/customer-portal',
      subject: 'guest-experience',
      action: 'read'
    },
    ...(isOrderManagementEnabled
      ? [
          {
            title: 'Order Management',
            icon: 'mynaui:cart',
            path: '/app/order-management',
            subject: 'order-management',
            action: 'read'
          }
        ]
      : []),
    ...(isMasterProductListEnabled
      ? [
          {
            title: 'Products',
            path: '/app/products',
            icon: 'material-symbols:contract-outline',
            subject: 'product',
            action: 'read'
          }
        ]
      : []),
    ...(isDigitalTrainingPlannerEnabled
      ? [
          {
            title: 'Training Management',
            icon: 'mdi:clipboard-text-outline',
            subject: 'digital-training-planner',
            action: 'read',
            children: [
              {
                title: 'Plan Settings',
                path: '/app/training-planner/catalog',
                icon: 'mdi:bookmark-multiple-outline',
                subject: 'digital-training-planner',
                action: 'manage'
              },
              {
                title: 'Template Plans',
                path: '/app/training-planner/templates',
                icon: 'mdi:clipboard-text-outline',
                subject: 'digital-training-planner',
                action: 'manage'
              },
              {
                title: 'Completion Records',
                path: '/app/reporting/training-planner',
                icon: 'mdi:chart-box-outline',
                subject: 'digital-training-planner',
                action: 'read'
              }
            ]
          }
        ]
      : []),
    ...(isFranchiseModuleEnabled
      ? [
          {
            title: GYG_NAV_LABELS.franchiseeHub,
            icon: 'mdi:account-group',
            subject: 'franchise-hub',
            action: 'read',
            children: [
              {
                title: 'Franchise List',
                path: '/app/franchise-hub',
                icon: 'mdi:account-group',
                subject: 'franchise-hub',
                action: 'read'
              }
            ]
          }
        ]
      : []),
    {
      title: GYG_NAV_LABELS.users,
      icon: 'tabler:users',
      path: '/app/user-management',
      subject: 'user-management',
      action: 'read',
      children: [
        {
          title: 'User Role',
          path: '/app/user-management',
          icon: 'bxs:offer',
          subject: 'user-management',
          action: 'manage'
        },

        // {
        //   title: 'Role Delegation',
        //   path: '/app/role-delegation',
        //   icon: 'mdi:account-switch',
        //   subject: 'user-management',
        //   action: 'manage'
        // },
        {
          title: 'User List',
          path: '/app/user-management/list',
          icon: 'mingcute:inventory-line',
          subject: 'user-management',
          action: 'read'
        },
        {
          title: 'User Profile',
          path: authData.user
            ? `/app/user-management/profile/?userId=${authData.user.id}`
            : '/app/user-management/profile',
          icon: 'mdi:account-circle',
          subject: 'user-management',
          action: 'read'
        },
        {
          title: 'History Logs',
          path: '/app/user-management/changes',
          icon: 'mingcute:inventory-line',
          subject: 'user-management',
          action: 'read'
        }
      ]
    },
    ...getSplitAdminNavGroups({ includeBhyveAdminRailVariant: true })

    // TODO: Re-enable GYG Oracle (Restaurant Insights / AI Insights) when ready.
    // ...(isRestaurantInsightsEnabled
    //   ? [
    //       {
    //         title: 'GYG Oracle',
    //         icon: 'tabler:device-analytics',
    //         subject: 'restaurant',
    //         action: 'read',
    //         children: [
    //           {
    //             title: 'Restaurant Insights',
    //             path: '/app/restaurant-insights',
    //             icon: 'tabler:chart-line',
    //             subject: 'restaurant',
    //             action: 'read'
    //           },
    //           {
    //             title: 'AI Insights',
    //             path: '/app/ai-insights',
    //             icon: 'tabler:robot',
    //             subject: 'restaurant',
    //             action: 'read'
    //           }
    //         ]
    //       }
    //     ]
    //   : []),
  ]
}
